(() => {
  if (window.walletReviewsInitialized) return;
  window.walletReviewsInitialized = true;
  const pageLocale = /^da/i.test(document.documentElement.lang || '') ? 'da-DK' : 'de-DE';
  const numberFormat = new Intl.NumberFormat(pageLocale, { maximumFractionDigits: 2 });
  const dateFormat = new Intl.DateTimeFormat(pageLocale, { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });
  const shortName = value => {
    const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length > 1 && /^(m|mr|mme|dr|selarl)\.?$/i.test(parts[0])) parts.shift();
    if (!parts.length) return '';
    const tidy = part => {
      if (part.includes('.') || (part.length > 1 && part.length <= 3 && part === part.toUpperCase())) return part;
      if (part !== part.toUpperCase() && part !== part.toLowerCase()) return part;
      return part.split('-').map(piece => piece.charAt(0).toUpperCase() + piece.slice(1).toLowerCase()).join('-');
    };
    let first = tidy(parts[0]);
    if (first.length === 1) first += '.';
    if (parts.length === 1) return first;
    const last = parts[parts.length - 1];
    if (/^([A-Za-z]\.)+$/.test(last)) return `${first} ${last}`;
    return `${first} ${last.replace(/[,.]/g, '').charAt(0).toUpperCase()}.`;
  };
  const fill = (message, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, String(value)), String(message || ''));
  const element = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = String(value);
    return node;
  };
  async function resolveOverlay(data, response) {
    if (!data || typeof data.base !== 'string' || !Array.isArray(data.parts)) return data;
    const folder = response.url.replace(/[^/]*$/, '');
    const fetchJson = name => fetch(folder + encodeURIComponent(name), { credentials: 'omit' })
      .then(result => (result.ok ? result.json() : null))
      .catch(() => null);
    const [base, ...parts] = await Promise.all([data.base, ...data.parts].map(fetchJson));
    if (!base || !Array.isArray(base.reviews)) throw new Error('Invalid reviews');
    const titles = {};
    const bodies = {};
    parts.forEach(part => {
      if (typeof part === 'string') {
        part.split('¶').forEach(record => {
          const [key, title, body] = record.split('§');
          if (!key || typeof body !== 'string') return;
          bodies[key] = body;
          if (title) titles[key] = title;
        });
        return;
      }
      if (!part || typeof part !== 'object') return;
      if (part.t && typeof part.t === 'object') Object.assign(titles, part.t);
      if (part.b && typeof part.b === 'object') Object.assign(bodies, part.b);
    });
    base.reviews.forEach(review => {
      if (!review || typeof review !== 'object') return;
      const key = String(review.id || '').slice(0, 8);
      if (typeof bodies[key] !== 'string') return;
      review.body_da = bodies[key];
      review.title_da = typeof titles[key] === 'string' ? titles[key] : '';
    });
    return {
      ...base,
      source_count: data.source_count ?? base.source_count,
      rating_count: data.rating_count ?? base.rating_count,
      average_rating: data.average_rating ?? base.average_rating
    };
  }
  function initialize(root) {
    if (root.dataset.reviewsInitialized) return;
    root.dataset.reviewsInitialized = 'true';
    const compact = root.dataset.reviewsCompact === 'true';
    const pageSize = compact ? 3 : 12;
    const labels = JSON.parse(root.querySelector('[data-reviews-labels]').textContent);
    const grid = root.querySelector('[data-reviews-grid]');
    const status = root.querySelector('[data-reviews-status]');
    const tools = root.querySelector('[data-reviews-tools]');
    const filter = root.querySelector('[data-reviews-filter]');
    filter.value = !compact && ['all', '5', '4', '3', '2', '1'].includes(root.dataset.defaultRatingFilter) ? root.dataset.defaultRatingFilter : 'all';
    const sort = root.querySelector('[data-reviews-sort]');
    const results = root.querySelector('[data-reviews-results]');
    const pagination = root.querySelector('[data-reviews-pagination]');
    const previous = root.querySelector('[data-reviews-previous]');
    const next = root.querySelector('[data-reviews-next]');
    const pageLabel = root.querySelector('[data-reviews-page]');
    const retry = root.querySelector('[data-reviews-retry]');
    let reviews = [];
    let currentPage = 1;
    let pending = false;
    function card(review) {
      const article = element('article', 'wallet-review');
      const top = element('div', 'wallet-review__top');
      const rating = element('p', 'wallet-review__rating');
      if (review.rating !== null) {
        const symbol = element('span', 'wallet-review__rating-symbol', '★');
        symbol.setAttribute('aria-hidden', 'true');
        rating.append(symbol, document.createTextNode(fill(labels.rating, { rating: review.rating })));
      } else rating.textContent = labels.unrated;
      top.append(rating);
      if (review.date !== null) {
        const time = element('time', 'wallet-review__date', dateFormat.format(review.date));
        time.dateTime = review.createdAt;
        top.append(time);
      }
      article.append(top, element('p', 'wallet-review__author', review.name || labels.anonymous));
      if (review.title) article.append(element('h3', '', review.title));
      if (review.body) article.append(element('p', 'wallet-review__body', review.body));
      return article;
    }
    function render(moveFocus = false) {
      const selected = compact ? 'all' : filter.value;
      const filtered = reviews.filter(review => selected === 'all' || review.rating === Number(selected));
      if (!compact) filtered.sort((left, right) => {
        if (sort.value === 'highest' || sort.value === 'lowest') {
          if (left.rating === null && right.rating !== null) return 1;
          if (right.rating === null && left.rating !== null) return -1;
          const difference = (right.rating || 0) - (left.rating || 0);
          if (difference) return sort.value === 'highest' ? difference : -difference;
        }
        if (left.date === null && right.date !== null) return 1;
        if (right.date === null && left.date !== null) return -1;
        const difference = (right.timestamp || 0) - (left.timestamp || 0);
        return (sort.value === 'oldest' ? -difference : difference) || left.index - right.index;
      });
      const pages = compact ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
      currentPage = compact ? 1 : Math.min(currentPage, pages);
      const start = (currentPage - 1) * pageSize;
      const visible = filtered.slice(start, start + pageSize);
      grid.replaceChildren(...visible.map(card));
      grid.setAttribute('aria-busy', 'false');
      results.hidden = false;
      results.textContent = filtered.length === 1 ? labels.results_one : filtered.length ? fill(labels.results, { count: numberFormat.format(filtered.length), start: numberFormat.format(start + 1), end: numberFormat.format(start + visible.length) }) : labels.empty;
      pagination.hidden = compact || filtered.length <= pageSize;
      previous.disabled = currentPage === 1;
      next.disabled = currentPage === pages;
      pageLabel.textContent = fill(labels.page, { current: currentPage, total: pages });
      if (moveFocus) grid.focus({ preventScroll: true });
    }
    function updateSummary(data) {
      const ratings = reviews.filter(review => review.rating !== null);
      const declaredCount = Number(data.source_count);
      const sourceCount = Number.isInteger(declaredCount) && declaredCount >= reviews.length ? declaredCount : reviews.length;
      const complete = sourceCount === reviews.length;
      const declaredAverage = Number(data.average_rating);
      const mean = complete && ratings.length ? ratings.reduce((sum, review) => sum + review.rating, 0) / ratings.length : declaredAverage;
      const total = root.querySelector('[data-reviews-total]');
      total.textContent = sourceCount === 1 ? labels.total_one : fill(labels.total, { count: numberFormat.format(sourceCount) });
      total.hidden = false;
      const ratedCount = complete ? ratings.length : Number(data.rating_count);
      const rated = root.querySelector('[data-reviews-rated]');
      rated.hidden = !(Number.isInteger(ratedCount) && ratedCount >= 0 && ratedCount < sourceCount);
      if (!rated.hidden) rated.textContent = ratedCount === 1 ? labels.rated_one : fill(labels.rated, { count: numberFormat.format(ratedCount) });
      const average = root.querySelector('[data-reviews-average]');
      average.hidden = !(Number.isFinite(mean) && mean >= 1 && mean <= 5);
      if (!average.hidden) root.querySelector('[data-reviews-average-value]').textContent = numberFormat.format(mean);
    }
    async function load() {
      if (pending) return;
      pending = true;
      status.hidden = false;
      status.textContent = labels.loading;
      retry.hidden = true;
      grid.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(root.dataset.reviewsUrl, { credentials: 'omit' });
        if (!response.ok) throw new Error('Unavailable reviews');
        const data = await resolveOverlay(await response.json(), response);
        if (!Array.isArray(data.reviews)) throw new Error('Invalid reviews');
        reviews = data.reviews.filter(review => review && typeof review === 'object').map((review, index) => {
          const rawRating = (typeof review.rating === 'number' || typeof review.rating === 'string') && review.rating !== '' ? Number(review.rating) : NaN;
          const rating = Number.isInteger(rawRating) && rawRating >= 1 && rawRating <= 5 ? rawRating : null;
          const createdAt = typeof review.created_at === 'string' ? review.created_at : '';
          const datePart = createdAt.slice(0, 10);
          const calendarDate = /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(createdAt) ? new Date(`${datePart}T00:00:00Z`) : null;
          const validDate = calendarDate && Number.isFinite(calendarDate.getTime()) && calendarDate.toISOString().slice(0, 10) === datePart;
          const candidate = validDate ? new Date(createdAt) : null;
          const date = candidate && Number.isFinite(candidate.getTime()) ? candidate : null;
          const pick = key => {
            const value = typeof review[`${key}_da`] === 'string' ? review[`${key}_da`] : review[`${key}_de`];
            return typeof value === 'string' ? value.trim() : '';
          };
          return { index, rating, createdAt, date, timestamp: date ? date.getTime() : null, name: shortName(review.reviewer_name), title: pick('title'), body: pick('body') };
        });
        updateSummary(data);
        tools.hidden = compact;
        status.hidden = true;
        render();
      } catch (error) {
        status.textContent = labels.error;
        retry.hidden = false;
        grid.setAttribute('aria-busy', 'false');
      } finally { pending = false; }
    }
    filter.addEventListener('change', () => { currentPage = 1; render(); });
    sort.addEventListener('change', () => { currentPage = 1; render(); });
    previous.addEventListener('click', () => { currentPage -= 1; render(true); });
    next.addEventListener('click', () => { currentPage += 1; render(true); });
    retry.addEventListener('click', load);
    load();
  }
  const initializeAll = scope => scope.querySelectorAll('[data-wallet-reviews]').forEach(initialize);
  initializeAll(document);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
})();